// client/src/components/NFTDetail.js
import React, { useState, useEffect } from 'react';
import { getNFTDetail, updateNFT, getImageUrl } from './api';
import './NFTDetail.css';

// Markdown을 HTML로 변환하는 간단한 함수 (Showdown 또는 Marked.js 같은 라이브러리를 사용하면 더 완벽함)
// 여기서는 임시로 간단한 Markdown 변환 로직을 추가하며, 
// 실제로는 npm install showdown 후 import showdown from 'showdown'; 를 사용하여 구현해야 합니다.
const convertMarkdownToHtml = (markdownText) => {
  if (!markdownText) return '';
  
  // 간단한 변환 규칙 (실제 라이브러리 대체용)
  let html = markdownText;
  
  // 1. 헤더 (##, # 등)
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

  // 2. 굵은 글씨 (**text**)
  html = html.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');
  
  // 3. 기울임꼴 (*text*)
  html = html.replace(/\*(.*?)\*/gim, '<em>$1</em>');
  
  // 4. 목록 (* item)
  html = html.replace(/^\* (.*$)/gim, '<li>$1</li>');
  html = html.replace(/^(<li>.*<\/li>)$/gim, '<ul>$1</ul>');
  html = html.replace(/<\/ul>(\s*)<ul>/gim, ''); // 목록이 연속되는 경우 처리

  // 5. 링크 ([text](url)) - 기존 description-link 클래스 적용
  html = html.replace(/\[([^\]]+)\]\(([^\)]+)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer" class="detailed-description-link">$1</a>');

  // 6. 줄바꿈을 <br>로 변환 (두 번의 줄바꿈은 <p>로 처리하기 위해 <br> 대신 <p>로 묶을 수도 있음)
  // 여기서는 기존 renderDescription의 줄바꿈 처리를 따라감
  html = html.split('\n').map(line => line.trim() ? line : '<br/>').join('\n');
  
  // 7. 한 줄 이상의 텍스트를 <p>로 묶기 (복잡하여 생략. 실제 라이브러리가 처리)
  // ...

  return html;
};

const NFTDetail = ({ nftId, onBack }) => {
  const [nft, setNft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [editForm, setEditForm] = useState({
    description: '',
    detailed_description: '',
    youtubeUri: '',
    nftImage: null,
    subImage: null,
    deleteSubImage: false
  });
  
  const [previewImages, setPreviewImages] = useState({
    nftImage: null,
    subImage: null
  });

  const [modalImage, setModalImage] = useState(null);

  useEffect(() => {
    loadNFTDetail();
  }, [nftId]);

  const loadNFTDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getNFTDetail(nftId);
      setNft(result.nft);
      setEditForm({
        description: result.nft.description || '',
        detailed_description: result.nft.detailed_description || '',
        youtubeUri: result.nft.youtubeUri || '',
        nftImage: null,
        subImage: null,
        deleteSubImage: false
      });
    } catch (err) {
      setError(err.error || 'NFT 정보를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setEditForm({
        description: nft.description || '',
        detailed_description: nft.detailed_description || '',
        youtubeUri: nft.youtubeUri || '',
        nftImage: null,
        subImage: null,
        deleteSubImage: false
      });
      setPreviewImages({
        nftImage: null,
        subImage: null
      });
    }
    setIsEditing(!isEditing);
  };

  const handleImageClick = (imageSrc) => {
    setModalImage(imageSrc);
  };

  const handleModalClose = () => {
    setModalImage(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const processImageFile = (file, imageType) => {
    // 이미지 파일 타입 체크
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }

    setEditForm(prev => ({
      ...prev,
      [imageType]: file
    }));
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImages(prev => ({
        ...prev,
        [imageType]: reader.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleImageChange = (e, imageType) => {
    const file = e.target.files[0];
    if (file) {
      processImageFile(file, imageType);
    }
  };

  const handleImageDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('drag-over');
  };

  const handleImageDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');
  };

  const handleImageDrop = (e, imageType) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      
      if (file.type.startsWith('image/')) {
        processImageFile(file, imageType);
        setError(''); // 에러 메시지 초기화
      } else {
        setError('이미지 파일만 업로드할 수 있습니다.');
      }
    }
  };

  const handleDeleteSubImage = () => {
    setEditForm(prev => ({
      ...prev,
      deleteSubImage: true,
      subImage: null
    }));
    setPreviewImages(prev => ({
      ...prev,
      subImage: null
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const formData = new FormData();
      formData.append('description', editForm.description);
      formData.append('detailed_description', editForm.detailed_description);
      formData.append('youtubeUri', editForm.youtubeUri);
      formData.append('deleteSubImage', editForm.deleteSubImage);

      if (editForm.nftImage) {
        formData.append('nftImage', editForm.nftImage);
      }

      if (editForm.subImage) {
        formData.append('subImage', editForm.subImage);
      }

      await updateNFT(nftId, formData);
      
      await loadNFTDetail();
      setIsEditing(false);
      setPreviewImages({
        nftImage: null,
        subImage: null
      });
      
      alert('NFT가 성공적으로 수정되었습니다.');
    } catch (err) {
      setError(err.error || 'NFT 수정 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // YouTube URL에서 비디오 ID 추출
  const extractYouTubeId = (url) => {
    if (!url) return null;
    
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  // 이미지 URI 패턴 감지
  const isImageUrl = (text) => {
    if (!text) return false;
    const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i;
    const httpPattern = /^https?:\/\/.+/i;
    return httpPattern.test(text) && imageExtensions.test(text);
  };

  // YouTube URI 패턴 감지
  const isYouTubeUrl = (text) => {
    if (!text) return false;
    return /(?:youtube\.com|youtu\.be)/i.test(text);
  };
 
  // HTML 코드 여부 확인
  const isHTMLContent = (text) => {
    if (!text) return false;
    const htmlPattern = /<\s*([a-z][a-z0-9]*)\b[^>]*>/i;
    return htmlPattern.test(text);
  };
  
  // Markdown 코드 여부 확인 (간단한 감지)
  const isMarkdownContent = (text) => {
    if (!text) return false;
    // 헤더, 목록, 링크, 굵은 글씨 등 Markdown 구문 감지
    const markdownPattern = /(^#+\s)|(\*\*\*?.*?\*\*\*?)|(\[.*?\]\(.*?\))/gim;
    return markdownPattern.test(text);
  };

  // Markdown 파일 확장자 확인
  const isMarkdownFile = (fileName) => {
    if (!fileName) return false;
    return fileName.endsWith('.md') || fileName.endsWith('.markdown');
  };

  // HTML 코드 정리 (불필요한 공백 제거)
  const cleanHTMLContent = (html) => {
    if (!html) return '';
    
    // HTML 태그 사이의 불필요한 줄바꿈과 공백 제거
    return html
      .replace(/>\s+</g, '><')  // 태그 사이의 공백 제거
      .trim();
  };

  // 드래그 오버(Drag Over) 시 기본 동작 방지
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  // 드롭(Drop) 시 파일 처리
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      const fileName = file.name.toLowerCase();
      
      let isSupported = false;
      let fileType = '';

      // HTML 파일 (.html, .htm) 확인
      if (file.type === 'text/html' || fileName.endsWith('.html') || fileName.endsWith('.htm')) {
        isSupported = true;
        fileType = 'HTML';
      } 
      // Markdown 파일 (.md, .markdown) 확인
      else if (file.type === 'text/markdown' || isMarkdownFile(fileName)) {
        isSupported = true;
        fileType = 'Markdown';
      }

      if (isSupported) {
        const reader = new FileReader();
        
        reader.onload = (event) => {
          const fileContent = event.target.result;
          
          // EditForm의 detailed_description 상태 업데이트
          setEditForm(prev => ({
            ...prev,
            detailed_description: fileContent
          }));

          alert(`${file.name} 파일의 내용이 Detailed Description에 로드되었습니다 (${fileType}).`);
        };
        
        reader.onerror = () => {
          setError('파일을 읽는 도중 오류가 발생했습니다.');
        };
        
        // 파일을 텍스트로 읽습니다.
        reader.readAsText(file);
        
      } else {
        setError('HTML 파일 (.html, .htm) 또는 Markdown 파일 (.md, .markdown)만 드롭할 수 있습니다.');
      }
    }
  };

  // Detailed Description 텍스트를 파싱하여 렌더링
  const renderDetailedDescription = (text) => {
    if (!text) return <p>상세 설명이 없습니다.</p>;
    
    let contentToRender = text;
    let className = "detailed-description-html-content";
    
    // Markdown 콘텐츠인 경우 HTML로 변환
    if (!isHTMLContent(text) && isMarkdownContent(text)) {
      contentToRender = convertMarkdownToHtml(text);
    }
    
    // HTML 또는 Markdown 변환 결과인 경우
    if (isHTMLContent(contentToRender) || isMarkdownContent(text)) {
      const cleanedHTML = cleanHTMLContent(contentToRender);
      return (
        <div 
          className={className}
          dangerouslySetInnerHTML={{ __html: cleanedHTML }}
        />
      );
    }
    
    // 기존 로직: 일반 텍스트 처리
    const lines = text.split('\n');
    const elements = [];
    
    lines.forEach((line, lineIndex) => {
      const trimmedLine = line.trim();
      
      // 이미지 URL인 경우
      if (isImageUrl(trimmedLine)) {
        elements.push(
          <div key={`img-${lineIndex}`} className="detailed-description-image-container">
            <img 
              src={trimmedLine} 
              alt={`Detailed Description image ${lineIndex + 1}`}
              className="detailed-description-image"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="image-error" style={{display: 'none'}}>
              이미지를 불러올 수 없습니다: {trimmedLine}
            </div>
          </div>
        );
      }
      // YouTube URL인 경우
      else if (isYouTubeUrl(trimmedLine)) {
        const videoId = extractYouTubeId(trimmedLine);
        if (videoId) {
          elements.push(
            <div key={`yt-${lineIndex}`} className="detailed-description-youtube-container">
              <iframe
                src={`https://www.youtube.com/embed/${videoId}`}
                title={`YouTube video ${lineIndex + 1}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="detailed-description-youtube"
              />
            </div>
          );
        } else {
          elements.push(
            <p key={`text-${lineIndex}`}>
              <a href={trimmedLine} target="_blank" rel="noopener noreferrer" className="detailed-description-link">
                {trimmedLine}
              </a>
            </p>
          );
        }
      }
      // 일반 URL인 경우
      else if (/^https?:\/\/.+/i.test(trimmedLine)) {
        elements.push(
          <p key={`text-${lineIndex}`}>
            <a href={trimmedLine} target="_blank" rel="noopener noreferrer" className="detailed-description-link">
              {trimmedLine}
            </a>
          </p>
        );
      }
      // 일반 텍스트인 경우 (URL 링크 처리 포함)
      else if (trimmedLine) {
        const urlPattern = /(https?:\/\/[^\s]+)/g;
        const parts = trimmedLine.split(urlPattern);
        
        elements.push(
          <p key={`text-${lineIndex}`}>
            {parts.map((part, partIndex) => {
              if (part.match(urlPattern)) {
                // 인라인 이미지 URL
                if (isImageUrl(part)) {
                  return (
                    <span key={partIndex} className="inline-image-wrapper">
                      <img 
                        src={part} 
                        alt={`Inline image ${partIndex}`}
                        className="inline-detailed-description-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </span>
                  );
                }
                // 인라인 YouTube URL
                else if (isYouTubeUrl(part)) {
                  return (
                    <a 
                      key={partIndex}
                      href={part}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="detailed-description-link youtube-inline-link"
                    >
                      🎥 {part}
                    </a>
                  );
                }
                // 일반 URL
                return (
                  <a 
                    key={partIndex}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="detailed-description-link"
                  >
                    {part}
                  </a>
                );
              }
              return <span key={partIndex}>{part}</span>;
            })}
          </p>
        );
      }
      // 빈 줄
      else {
        elements.push(<br key={`br-${lineIndex}`} />);
      }
    });
    
    return <div className="detailed-description-rendered">{elements}</div>;
  };

  if (loading) {
    return (
      <div className="nft-detail-container">
        <button onClick={onBack} className="back-btn">← 뒤로가기</button>
        <div className="loading">NFT 정보를 불러오는 중...</div>
      </div>
    );
  }

  if (error && !nft) {
    return (
      <div className="nft-detail-container">
        <button onClick={onBack} className="back-btn">← 뒤로가기</button>
        <div className="error-message">{error}</div>
        <button onClick={loadNFTDetail} className="retry-btn">다시 시도</button>
      </div>
    );
  }

  return (
    <div className="nft-detail-container">
      <div className="header-actions">
        <button onClick={onBack} className="back-btn">← 뒤로가기</button>
        <div className="action-buttons">
          {isEditing ? (
            <>
              <button 
                onClick={handleSave} 
                className="save-btn"
                disabled={saving}
              >
                {saving ? '저장 중...' : '💾 저장'}
              </button>
              <button 
                onClick={handleEditToggle} 
                className="cancel-btn"
                disabled={saving}
              >
                취소
              </button>
            </>
          ) : (
            <button onClick={handleEditToggle} className="edit-btn">
              ✏️ 수정
            </button>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      <div className="nft-detail-content">
        <div className="top-info">
          <div className="top-info-item">
            <h3>{nft.collectionName}</h3>
          </div>
          <div className="top-info-item">
            <h3>Token #{nft.tokenId}</h3>
          </div>
        </div>

        {/* Description 섹션 */}
        <div className="description-section">
          <h3>Description</h3>
          {isEditing ? (
            <input
              type="text"
              name="description"
              value={editForm.description}
              onChange={handleInputChange}
              placeholder="NFT 제목/요약 설명을 입력하세요"
              className="description-input"
            />
          ) : (
            <div className="description-content">
              {nft.description || '제목/요약 설명이 없습니다.'}
            </div>
          )}
        </div>

        <div className="youtube-section">
          <h3>Youtube link</h3>
          {isEditing ? (
            <input
              type="text"
              name="youtubeUri"
              value={editForm.youtubeUri}
              onChange={handleInputChange}
              placeholder="YouTube 링크를 입력하세요"
              className="youtube-input"
            />
          ) : (
            <div className="youtube-link-container">
              {nft.youtubeUri ? (
                <div className="youtube-link-content">
                  <div className="youtube-url">
                    <span className="url-icon">🎥</span>
                    <a 
                      href={nft.youtubeUri} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="youtube-url-text"
                    >
                      {nft.youtubeUri}
                    </a>
                  </div>
                  <a
                    href={nft.youtubeUri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="youtube-open-btn"
                  >
                    열기
                  </a>
                </div>
              ) : (
                <div className="no-video">
                  <p>등록된 YouTube 링크가 없습니다.</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="nft-detail-body">
          <div className="left-section">
            <div className="main-image">
              <h3>NFT Image</h3> 
              <div 
                className="image-drop-zone"
                onDragOver={handleImageDragOver}
                onDragLeave={handleImageDragLeave}
                onDrop={(e) => handleImageDrop(e, 'nftImage')}
              >
                <img 
                  src={previewImages.nftImage || getImageUrl(nft.nftImageUri)} 
                  alt={`${nft.collectionName} #${nft.tokenId}`}
                  onClick={() => handleImageClick(previewImages.nftImage || getImageUrl(nft.nftImageUri))}
                  style={{ cursor: 'pointer' }}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/600?text=Image+Not+Found';
                  }}
                />
              </div>
              {isEditing && (
                <div className="image-upload-control">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'nftImage')}
                    id="nftImageInput"
                    className="file-input"
                  />
                  <label htmlFor="nftImageInput" className="file-label">
                    📁 새 이미지 선택  
                  </label>
                </div>
              )}
            </div>

            <div className="sub-image">
              <h3>Sub Image</h3> 
              <div 
                className="image-drop-zone"
                onDragOver={handleImageDragOver}
                onDragLeave={handleImageDragLeave}
                onDrop={(e) => handleImageDrop(e, 'subImage')}
              >
                {editForm.deleteSubImage ? (
                  <div className="no-image">
                    <p>삭제될 이미지</p>
                  </div>
                ) : previewImages.subImage || nft.subImageUri ? (
                  <img 
                    src={previewImages.subImage || getImageUrl(nft.subImageUri)} 
                    alt="Sub Image"
                    onClick={() => handleImageClick(previewImages.subImage || getImageUrl(nft.subImageUri))}
                    style={{ cursor: 'pointer' }}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/600?text=Image+Not+Found';
                    }}
                  />
                ) : (
                  <div className="no-image">
                    <p>등록된 서브 이미지가 없습니다</p>
                  </div>
                )}
              </div>
              {isEditing && (
                <div className="image-upload-control">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'subImage')}
                    id="subImageInput"
                    className="file-input"
                  />
                  <label htmlFor="subImageInput" className="file-label">
                    📁 이미지 선택 또는 드래그
                  </label>
                  {(nft.subImageUri || previewImages.subImage) && !editForm.deleteSubImage && (
                    <button 
                      onClick={handleDeleteSubImage}
                      className="delete-image-btn"
                    >
                      🗑️ 삭제
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="right-section">
            {/* Detailed Description */}
            <div className="detailed-description-box">
              <h3>Detailed Description</h3>
              {isEditing ? (
                <textarea
                  name="detailed_description"
                  value={editForm.detailed_description}
                  onChange={handleInputChange}
                  placeholder="NFT 설명을 입력하세요&#10;&#10;💡 팁:&#10;- 이미지 URL을 한 줄에 입력하면 이미지로 표시됩니다&#10;- YouTube URL을 한 줄에 입력하면 동영상이 임베드됩니다&#10;- HTML 또는 Markdown 파일을 드래그 앤 드롭하여 내용을 로드할 수 있습니다."
                  className="detailed-description-textarea"
                  rows="15"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                />
              ) : (
                <div className="detailed-description-content">
                  {renderDetailedDescription(nft.detailed_description)}
                </div>
              )}
            </div>
          </div>
        </div>

        
      </div>

      {modalImage && (
        <div className="image-modal" onClick={handleModalClose}>
          <div className="modal-content">
            <img src={modalImage} alt="Enlarged view" />
          </div>
        </div>
      )}
    </div>
  );
};

export default NFTDetail;